import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and a JS-measured
 * height custom property for the expand/collapse animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * A ResizeObserver keeps --disclosure-content-height in sync with the content's natural height,
 * which the CSS transitions between 0 and that value when the inert attribute is toggled.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { disclosureContent: content } = this.refs;
    const contentInner = content.firstElementChild;
    if (!contentInner) return;

    // Measure synchronously up front: the ResizeObserver's first callback is async, so without
    // this the very first open (before it has fired) would fall back to the CSS `auto` default,
    // which can't transition smoothly.
    content.style.setProperty('--disclosure-content-height', `${contentInner.scrollHeight}px`);

    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      content.style.setProperty('--disclosure-content-height', `${blockSize}px`);
    });
    this.#resizeObserver.observe(contentInner);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.toggleAttribute('inert', expanded);
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
